import { motion } from "framer-motion";
import { TrendingUp, Award, MapPin, Users, Zap, Target } from "lucide-react";

const growthPaths = [
  {
    icon: TrendingUp,
    title: "Senior Production Roles",
    description: "Advance into senior positions with higher earnings potential.",
  },
  {
    icon: Users,
    title: "Team Leadership",
    description: "Lead your own team and earn from both personal and team production.",
  },
  {
    icon: MapPin,
    title: "Market Expansion",
    description: "Help open new markets and territories as CityWorth grows.",
  },
  {
    icon: Award,
    title: "Multi-State Opportunities",
    description: "Expand your reach across multiple states and regions.",
  },
];

const differentiators = [
  { icon: Zap, label: "Faster Deal Flow" },
  { icon: Target, label: "Higher Close Rates" },
  { icon: Users, label: "Better Client Experience" },
  { icon: TrendingUp, label: "Accelerated Development" },
];

const SUCCESS_IMAGE = "https://images.unsplash.com/photo-1758691737138-7b9b1884b1db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGVtcGxveWVlcyUyMHRlYW0lMjBvZmZpY2UlMjBjZWxlYnJhdGluZyUyMHN1Y2Nlc3N8ZW58MHx8fHwxNzcwNDEwMzgzfDA&ixlib=rb-4.1.0&q=85";

const CareerGrowthSection = () => {
  return (
    <section className="py-24 md:py-32 relative bg-white" data-testid="career-growth-section">
      <div className="container-main relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-semibold tracking-widest uppercase text-red-600 mb-4 block">
            Career Growth
          </span>
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-slate-900">
            Build a{" "}
            <span className="gradient-text">High-Growth Career</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            CityWorth offers structured advancement paths for ambitious professionals
            who want to scale faster and earn more.
          </p>
        </motion.div>

        {/* Growth paths grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {growthPaths.map((path, index) => {
            const Icon = path.icon;
            return (
              <motion.div
                key={path.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-red-300 transition-all card-hover text-center shadow-sm"
                data-testid={`growth-path-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-100 to-blue-100 flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-7 h-7 text-slate-700" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{path.title}</h3>
                <p className="text-slate-600 text-sm">{path.description}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* What makes CityWorth different */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-slate-50 rounded-2xl p-8 md:p-12 border border-slate-200"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                Why CityWorth is Different
              </h3>
              <p className="text-slate-600 leading-relaxed mb-6">
                CityWorth operates as a fully integrated homebuying platform rather than
                a traditional brokerage or mortgage bank. Real estate and mortgage operate
                together, creating unprecedented advantages.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {differentiators.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center border border-slate-200">
                        <Icon className="w-4 h-4 text-slate-700" />
                      </div>
                      <span className="text-slate-700 text-sm font-medium">{item.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              <div className="image-card rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={SUCCESS_IMAGE} 
                  alt="Happy team celebrating success"
                  className="w-full h-64 object-cover"
                />
              </div>

              {/* Stats overlay */}
              <motion.div
                className="absolute -bottom-4 -right-4 bg-white rounded-xl p-4 shadow-lg border border-slate-200"
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="text-center">
                  <div className="text-3xl font-bold gradient-text">10+ yrs</div>
                  <div className="text-xs text-slate-500">Experience in 3 years</div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Positioning statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <blockquote className="text-xl md:text-2xl font-medium text-slate-800 max-w-3xl mx-auto leading-relaxed">
            "CityWorth is a career accelerator designed for ambitious professionals who want
            to scale faster, close more business, and operate inside a fully supported system."
          </blockquote>
          <p className="text-slate-500 mt-4">— Our Mission</p>
        </motion.div>
      </div>
    </section>
  );
};

export default CareerGrowthSection;
